import { useEffect, useState, useRef } from 'react';

/**
 * Précharge toute la séquence d'images d'une vue.
 * Retourne { isLoading, progress (0-100) }
 *
 * @param {string} baseImagePath  - Chemin avec frame 0000 (ex: /helio/.../jpg-1440/CAM.0000.webp)
 * @param {number} lastFrameNumber - Nombre total de frames
 * @param {number} concurrency    - Nombre de téléchargements parallèles (défaut: 6)
 */
const useSequenceLoader = (baseImagePath, lastFrameNumber, concurrency = 6) => {
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const abortRef = useRef(false);

  useEffect(() => {
    if (!baseImagePath || lastFrameNumber === null) return;

    abortRef.current = false;
    setIsLoading(true);
    setProgress(0);

    const total = lastFrameNumber + 1;
    let loaded = 0;

    const loadFrame = (frameNum) => {
      return new Promise((resolve) => {
        if (abortRef.current) { resolve(); return; }
        const img = new Image();
        img.onload = () => {
          loaded++;
          if (!abortRef.current) {
            setProgress(Math.round((loaded / total) * 100));
          }
          resolve();
        };
        img.onerror = () => { loaded++; resolve(); }; // on avance même en cas d'erreur
        img.src = baseImagePath.replace(/\d{4}\./, `${frameNum.toString().padStart(4, '0')}.`);
      });
    };

    // Traitement par batch concurrent
    const frames = Array.from({ length: total }, (_, i) => i);
    let index = 0;

    const worker = async () => {
      while (index < frames.length && !abortRef.current) {
        const i = index++;
        await loadFrame(frames[i]);
      }
    };

    const workers = Array.from({ length: Math.min(concurrency, total) }, worker);

    Promise.all(workers).then(() => {
      if (!abortRef.current) {
        setIsLoading(false);
        setProgress(100);
      }
    });

    return () => {
      abortRef.current = true;
      setIsLoading(false);
    };
  }, [baseImagePath, lastFrameNumber]);

  return { isLoading, progress };
};

export default useSequenceLoader;
